// Song Review Service — DJ verdict layer on top of songAnalyses Firestore collection
import { doc, getDoc, setDoc, getDocs, collection, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

export interface SongReview {
  id: string;
  title: string;
  artist: string;
  summary: string;
  profanityAnalysis: string;
  hasProfanity: boolean;
  profanityLevel: 'none' | 'mild' | 'moderate' | 'heavy';
  analyzedAt: string;
  model: string;
  djVerdict?: 'passed' | 'blocked';
  djReviewedAt?: string;
  djReviewedBy?: string;
}

// Match the cloud function's getSongAnalysisId using Web Crypto API
export async function getSongReviewId(title: string, artist: string): Promise<string> {
  const normalized = `${title.toLowerCase().trim()}|${artist.toLowerCase().trim()}`;
  const encoded = new TextEncoder().encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex.substring(0, 40);
}

// Set or update DJ verdict on an existing analysis doc
export async function setSongVerdict(
  title: string,
  artist: string,
  verdict: 'passed' | 'blocked' | null,
  djUserId: string
): Promise<void> {
  const docId = await getSongReviewId(title, artist);
  const ref = doc(db, 'songAnalyses', docId);
  if (verdict === null) {
    // Clear verdict
    await setDoc(ref, {
      djVerdict: null,
      djReviewedAt: null,
      djReviewedBy: null,
    }, { merge: true });
  } else {
    await setDoc(ref, {
      djVerdict: verdict,
      djReviewedAt: new Date().toISOString(),
      djReviewedBy: djUserId,
    }, { merge: true });
  }
}

// Get verdict for a specific song
export async function getSongVerdict(title: string, artist: string): Promise<'passed' | 'blocked' | null> {
  const docId = await getSongReviewId(title, artist);
  const ref = doc(db, 'songAnalyses', docId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data().djVerdict || null;
}

export interface SavedSongAnalysis {
  summary: string;
  profanityAnalysis: string;
  hasProfanity: boolean;
  profanityLevel: SongReview['profanityLevel'];
}

const PROFANITY_LEVELS: SongReview['profanityLevel'][] = ['none', 'mild', 'moderate', 'heavy'];

// The analysis already saved for a song, or null when none was saved. A doc holding only a DJ verdict is not an analysis.
export async function getSavedSongAnalysis(title: string, artist: string): Promise<SavedSongAnalysis | null> {
  const docId = await getSongReviewId(title, artist);
  const snap = await getDoc(doc(db, 'songAnalyses', docId));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (typeof data.summary !== 'string' || !data.summary.trim()) return null;
  if (typeof data.profanityAnalysis !== 'string' || !PROFANITY_LEVELS.includes(data.profanityLevel)) return null;
  return {
    summary: data.summary,
    profanityAnalysis: data.profanityAnalysis,
    hasProfanity: data.hasProfanity === true,
    profanityLevel: data.profanityLevel,
  };
}

// Get all analyzed songs (for Song Library page)
export async function getAllAnalyzedSongs(): Promise<SongReview[]> {
  const snapshot = await getDocs(collection(db, 'songAnalyses'));
  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      title: data.title || '',
      artist: data.artist || '',
      summary: data.summary || '',
      profanityAnalysis: data.profanityAnalysis || '',
      hasProfanity: data.hasProfanity === true,
      profanityLevel: data.profanityLevel || 'none',
      analyzedAt: data.analyzedAt instanceof Timestamp
        ? data.analyzedAt.toDate().toISOString()
        : data.analyzedAt || '',
      model: data.model || '',
      djVerdict: data.djVerdict || undefined,
      djReviewedAt: data.djReviewedAt instanceof Timestamp
        ? data.djReviewedAt.toDate().toISOString()
        : data.djReviewedAt || undefined,
      djReviewedBy: data.djReviewedBy || undefined,
    };
  });
}

// Check if a song is blocked (for attendee submit flow)
export async function isSongBlocked(title: string, artist: string): Promise<boolean> {
  const verdict = await getSongVerdict(title, artist);
  return verdict === 'blocked';
}

// Batch-load verdicts for a list of songs (for inline indicators)
export async function batchGetVerdicts(
  songs: Array<{ title: string; artist: string }>
): Promise<Map<string, 'passed' | 'blocked'>> {
  const verdictMap = new Map<string, 'passed' | 'blocked'>();
  // Load in parallel, capped to avoid excessive reads
  const unique = new Map<string, { title: string; artist: string }>();
  for (const s of songs) {
    const key = `${s.title.toLowerCase().trim()}|${s.artist.toLowerCase().trim()}`;
    if (!unique.has(key)) unique.set(key, s);
  }

  const entries = Array.from(unique.entries());
  const results = await Promise.all(
    entries.map(async ([key, s]) => {
      const docId = await getSongReviewId(s.title, s.artist);
      const ref = doc(db, 'songAnalyses', docId);
      const snap = await getDoc(ref);
      if (snap.exists() && snap.data().djVerdict) {
        return [key, snap.data().djVerdict as 'passed' | 'blocked'] as const;
      }
      return null;
    })
  );

  for (const r of results) {
    if (r) verdictMap.set(r[0], r[1]);
  }

  return verdictMap;
}
